import type { ElementRegistry } from "../core/element-registry"
import { ContainerRenderer } from "./container-renderer"
import { TextRenderer } from "./text-renderer"
import { ImageRenderer } from "./image-renderer"
import { ButtonRenderer } from "./button-renderer"
import { BadgeRenderer } from "./badge-renderer"
import { IconRenderer } from "./icon-renderer"
import { CardRenderer } from "./card-renderer"
import { InputRenderer } from "./input-renderer"
import { SeparatorRenderer } from "./separator-renderer"
import { SkeletonRenderer } from "./skeleton-renderer"
import { TabsRenderer } from "./tabs-renderer"
import { TabTriggerRenderer } from "./tab-trigger-renderer"
import { BlockRefRenderer } from "./block-ref-renderer"
// Phase 1: Form renderers
import { LabelRenderer } from "./label-renderer"
import { TextareaRenderer } from "./textarea-renderer"
import { CheckboxRenderer } from "./checkbox-renderer"
import { RadioRenderer } from "./radio-renderer"
import { SwitchRenderer } from "./switch-renderer"
import { SliderRenderer } from "./slider-renderer"
import { SelectRenderer } from "./select-renderer"
import { FormRenderer } from "./form-renderer"
// Phase 2: Overlay & Feedback renderers
import { DialogRenderer } from "./dialog-renderer"
import { AlertDialogRenderer } from "./alert-dialog-renderer"
import { SheetRenderer } from "./sheet-renderer"
import { DrawerRenderer } from "./drawer-renderer"
import { PopoverRenderer } from "./popover-renderer"
import { DropdownMenuRenderer } from "./dropdown-menu-renderer"
import { AlertRenderer } from "./alert-renderer"
import { ProgressRenderer } from "./progress-renderer"
import { ToastRenderer } from "./toast-renderer"
import { NavigationMenuRenderer } from "./navigation-menu-renderer"
// Phase 3: Data Display renderers
import { AccordionRenderer } from "./accordion-renderer"
import { AccordionItemRenderer } from "./accordion-item-renderer"
import { TableRenderer } from "./table-renderer"
import { DataTableRenderer } from "./data-table-renderer"
import { ChartRenderer } from "./chart-renderer"
import { CarouselRenderer } from "./carousel-renderer"
// Phase 4: Missing renderers (discovered via zero-base verification)
import { ListRenderer } from "./list-renderer"
import { AvatarRenderer } from "./avatar-renderer"
import { LinkRenderer } from "./link-renderer"
// Phase 6: Interactive renderers
import { TooltipRenderer } from "./tooltip-renderer"
// Phase 7: Navigation & Form renderers
import { BreadcrumbRenderer } from "./breadcrumb-renderer"
import { PaginationRenderer } from "./pagination-renderer"
import { DatePickerRenderer } from "./date-picker-renderer"
import { CalendarRenderer } from "./calendar-renderer"
import { ComboboxRenderer } from "./combobox-renderer"
// Phase 8: Feedback & Collapsible renderers
import { CollapsibleRenderer } from "./collapsible-renderer"
// Phase 9: Advanced interactive renderers
import { CommandRenderer } from "./command-renderer"
import { HoverCardRenderer } from "./hover-card-renderer"
import { InputOtpRenderer } from "./input-otp-renderer"
import { MenubarRenderer } from "./menubar-renderer"
import { ResizableRenderer } from "./resizable-renderer"
import { ToggleRenderer } from "./toggle-renderer"
import { ToggleGroupRenderer } from "./toggle-group-renderer"
// Phase 10: Flutter-exclusive interactive renderers
import { MonthPickerRenderer } from "./month-picker-renderer"
import { StarRatingRenderer } from "./star-rating-renderer"
import { ColorPickerRenderer } from "./color-picker-renderer"
import { ChipInputRenderer } from "./chip-input-renderer"
import { PhoneInputRenderer } from "./phone-input-renderer"
import { StepperRenderer } from "./stepper-renderer"
import { TimelineRenderer } from "./timeline-renderer"
import { TreeRenderer } from "./tree-renderer"
// Phase 11: Sidebar renderers
import { SidebarRenderer, SidebarInsetRenderer, SidebarTriggerRenderer } from "./sidebar-renderer"

export function registerAllRenderers(registry: ElementRegistry): void {
  // Existing renderers
  registry.register("container", ContainerRenderer)
  registry.register("section", ContainerRenderer)
  // HTML5 semantic element aliases — render as container with appropriate htmlTag
  registry.register("header", ContainerRenderer)
  registry.register("footer", ContainerRenderer)
  registry.register("nav", ContainerRenderer)
  registry.register("main", ContainerRenderer)
  registry.register("aside", ContainerRenderer)
  registry.register("article", ContainerRenderer)
  registry.register("text", TextRenderer)
  registry.register("image", ImageRenderer)
  registry.register("button", ButtonRenderer)
  registry.register("badge", BadgeRenderer)
  registry.register("icon", IconRenderer)
  registry.register("card", CardRenderer)
  registry.register("input", InputRenderer)
  registry.register("separator", SeparatorRenderer)
  registry.register("divider", SeparatorRenderer)
  registry.register("skeleton", SkeletonRenderer)
  registry.register("tabs", TabsRenderer)
  registry.register("tab-trigger", TabTriggerRenderer)
  registry.register("block-ref", BlockRefRenderer)
  // Phase 1: Form renderers
  registry.register("label", LabelRenderer)
  registry.register("textarea", TextareaRenderer)
  registry.register("checkbox", CheckboxRenderer)
  registry.register("radio", RadioRenderer)
  registry.register("switch", SwitchRenderer)
  registry.register("slider", SliderRenderer)
  registry.register("select", SelectRenderer)
  registry.register("form", FormRenderer)
  // Phase 2: Overlay & Feedback renderers
  registry.register("dialog", DialogRenderer)
  registry.register("alert-dialog", AlertDialogRenderer)
  registry.register("sheet", SheetRenderer)
  registry.register("drawer", DrawerRenderer)
  registry.register("popover", PopoverRenderer)
  registry.register("dropdown-menu", DropdownMenuRenderer)
  registry.register("alert", AlertRenderer)
  registry.register("progress", ProgressRenderer)
  registry.register("radial-progress", ProgressRenderer)
  registry.register("toast", ToastRenderer)
  registry.register("navigation-menu", NavigationMenuRenderer)
  // Phase 3: Data Display renderers
  registry.register("accordion", AccordionRenderer)
  registry.register("accordion-item", AccordionItemRenderer)
  registry.register("table", TableRenderer)
  registry.register("data-table", DataTableRenderer)
  registry.register("chart", ChartRenderer)
  registry.register("carousel", CarouselRenderer)
  // Phase 4: Missing renderers
  registry.register("list", ListRenderer)
  registry.register("avatar", AvatarRenderer)
  // Phase 5: Navigation
  registry.register("link", LinkRenderer)
  // Phase 6: Interactive renderers
  registry.register("tooltip", TooltipRenderer)
  // Phase 7: Navigation & Form renderers
  registry.register("breadcrumb", BreadcrumbRenderer)
  registry.register("pagination", PaginationRenderer)
  registry.register("date-picker", DatePickerRenderer)
  registry.register("calendar", CalendarRenderer)
  registry.register("combobox", ComboboxRenderer)
  // Phase 8: Feedback & Collapsible renderers
  registry.register("collapsible", CollapsibleRenderer)
  // Phase 9: Advanced interactive renderers
  registry.register("command", CommandRenderer)
  registry.register("hover-card", HoverCardRenderer)
  registry.register("input-otp", InputOtpRenderer)
  registry.register("menubar", MenubarRenderer)
  registry.register("resizable", ResizableRenderer)
  registry.register("toggle", ToggleRenderer)
  registry.register("toggle-group", ToggleGroupRenderer)
  // Phase 10: Flutter-exclusive interactive renderers
  registry.register("star-rating", StarRatingRenderer)
  registry.register("color-picker", ColorPickerRenderer)
  registry.register("chip-input", ChipInputRenderer)
  registry.register("phone-input", PhoneInputRenderer)
  registry.register("stepper", StepperRenderer)
  registry.register("timeline", TimelineRenderer)
  registry.register("tree", TreeRenderer)
  // Phase 10: Month picker
  registry.register("month-picker", MonthPickerRenderer)
  // Phase 11: Sidebar
  registry.register("sidebar", SidebarRenderer)
  registry.register("sidebar-inset", SidebarInsetRenderer)
  registry.register("sidebar-trigger", SidebarTriggerRenderer)
}

export { ElementRenderer } from "./element-renderer"
export { ContainerRenderer } from "./container-renderer"
export { TextRenderer } from "./text-renderer"
export { ImageRenderer } from "./image-renderer"
export { ButtonRenderer } from "./button-renderer"
export { BadgeRenderer } from "./badge-renderer"
export { IconRenderer } from "./icon-renderer"
export { CardRenderer } from "./card-renderer"
export { InputRenderer } from "./input-renderer"
export { SeparatorRenderer } from "./separator-renderer"
export { SkeletonRenderer } from "./skeleton-renderer"
export { LabelRenderer } from "./label-renderer"
export { TextareaRenderer } from "./textarea-renderer"
export { CheckboxRenderer } from "./checkbox-renderer"
export { RadioRenderer } from "./radio-renderer"
export { SwitchRenderer } from "./switch-renderer"
export { SliderRenderer } from "./slider-renderer"
export { SelectRenderer } from "./select-renderer"
export { FormRenderer } from "./form-renderer"
export { DialogRenderer } from "./dialog-renderer"
export { AlertDialogRenderer } from "./alert-dialog-renderer"
export { SheetRenderer } from "./sheet-renderer"
export { DrawerRenderer } from "./drawer-renderer"
export { PopoverRenderer } from "./popover-renderer"
export { DropdownMenuRenderer } from "./dropdown-menu-renderer"
export { AlertRenderer } from "./alert-renderer"
export { ProgressRenderer } from "./progress-renderer"
export { ToastRenderer } from "./toast-renderer"
export { NavigationMenuRenderer } from "./navigation-menu-renderer"
export { AccordionRenderer } from "./accordion-renderer"
export { AccordionItemRenderer } from "./accordion-item-renderer"
export { TableRenderer } from "./table-renderer"
export { DataTableRenderer } from "./data-table-renderer"
export { ChartRenderer } from "./chart-renderer"
export { CarouselRenderer } from "./carousel-renderer"
export { ListRenderer } from "./list-renderer"
export { AvatarRenderer } from "./avatar-renderer"
export { LinkRenderer } from "./link-renderer"
export { TooltipRenderer } from "./tooltip-renderer"
export { BreadcrumbRenderer } from "./breadcrumb-renderer"
export { PaginationRenderer } from "./pagination-renderer"
export { DatePickerRenderer } from "./date-picker-renderer"
export { CalendarRenderer } from "./calendar-renderer"
export { ComboboxRenderer } from "./combobox-renderer"
export { CollapsibleRenderer } from "./collapsible-renderer"
export { CommandRenderer } from "./command-renderer"
export { HoverCardRenderer } from "./hover-card-renderer"
export { InputOtpRenderer } from "./input-otp-renderer"
export { MenubarRenderer } from "./menubar-renderer"
export { ResizableRenderer } from "./resizable-renderer"
export { ToggleRenderer } from "./toggle-renderer"
export { ToggleGroupRenderer } from "./toggle-group-renderer"
export { StarRatingRenderer } from "./star-rating-renderer"
export { ColorPickerRenderer } from "./color-picker-renderer"
export { ChipInputRenderer } from "./chip-input-renderer"
export { PhoneInputRenderer } from "./phone-input-renderer"
export { StepperRenderer } from "./stepper-renderer"
export { TimelineRenderer } from "./timeline-renderer"
export { TreeRenderer } from "./tree-renderer"
export { MonthPickerRenderer } from "./month-picker-renderer"
export { SidebarRenderer, SidebarInsetRenderer, SidebarTriggerRenderer } from "./sidebar-renderer"
